import { inject, Injectable } from "@angular/core";
import { CommandeClientApiService } from "./commande-client-api.service";
import {
  CommandeClientState,
  CommandeClientStoreService,
} from "./commande-client-store.service";
import { EntityFacadeService } from "../../../../shared/store/entity-store/entity-facade.service";
import {
  EntityMetadata,
  QueryParam,
} from "../../../../shared/store/store-types.model";
import { CommandeClient } from "../../models/commande-client.model";
import {
  Untrack,
  UpdateStatus,
} from "../../../../shared/store/utils/store-utils.service";
import { CommandeClientLigne } from "../../models/commande-client-ligne";
import { firstValueFrom } from "rxjs";
import { CommandeClientEtat } from "../../enums/etat-commande-client.enum";
import { CommandeClientMailPayload } from "../../models/commande-client-mail-payload.model";
import { Tiers } from "../../../tiers/models/tiers.model";
import { VracExpeditionItem } from "../../../expedition-commande/models/vrac-expedition.model";
import { safeFirstValueFrom } from "../../../../shared/utils/safe-first-value-from";

@Injectable({ providedIn: "root" })
export class CommandeClientFacadeService extends EntityFacadeService<
  CommandeClient,
  CommandeClientApiService,
  CommandeClientState,
  EntityMetadata,
  CommandeClientStoreService
> {
  constructor() {
    const api = inject(CommandeClientApiService);
    const store = inject(CommandeClientStoreService);
    super(api, store);
    store.initStatus(CommandeClientFacadeService);
  }

  @Untrack
  @UpdateStatus
  async loadById(id: string, query?: QueryParam) {
    const commande = await super.loadById(id, query);
    if (commande) {
      this.store
        .getWritableState()
        .commandeClient.set({ ...commande(), lignes: [...commande().lignes] });
    }
  }

  @Untrack
  @UpdateStatus
  async add(commande: CommandeClient) {
    const saved = await super.add(commande);
    this.store
      .getWritableState()
      .commandeClient.set({ ...saved(), lignes: [...saved().lignes] });
    return saved;
  }

  @Untrack
  @UpdateStatus
  async upsert(commande: CommandeClient) {
    const saved = await super.upsert({ ...commande, lignes: undefined });
    this.store
      .getWritableState()
      .commandeClient.set({ ...saved(), lignes: [...saved().lignes] });
    return saved;
  }

  @Untrack
  @UpdateStatus
  async updateCommandeLignes(
    lignes: CommandeClientLigne[],
    dateLivraisonPrevue: Date,
    commandeId: string,
  ) {
    const commande = await this.api.updateCommandeLignes(
      lignes,
      dateLivraisonPrevue,
      commandeId,
    );
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
      this.store
        .getWritableState()
        .commandeClient.set({ ...commande, lignes: [...commande.lignes] });
    }
    return commande;
  }

  @Untrack
  @UpdateStatus
  async loadAllFilter(filters: {
    etats: CommandeClientEtat[];
    etatsExpedition?: string[];
    etatsFacturation?: string[];
    etatsPreparation?: string[];
    search: string;
    sort: string;
    entrepotId?: string;
    entrepotIds?: string[];
    includeSansEntrepot?: boolean;
    page?: number;
  }) {
    const page = filters.page ?? 0;
    if (page === 0) {
      this.store.removeAll();
    }
    const commandes = await firstValueFrom(this.api.loadAllFilter(filters));
    this.store.addMany(commandes);
    return commandes;
  }

  @Untrack
  @UpdateStatus
  async sendMail(a: CommandeClientMailPayload) {
    await firstValueFrom(this.api.sendMail(a));
  }

  async updateDocumentListeId(commandeId: string, documentListId: string) {
    await this.api.updateDocumentListeId(commandeId, documentListId);
  }

  updateTiers(tiers: Tiers) {
    this.elements().forEach((element) => {
      const commande = element();
      if (commande.tiersId === tiers.id) {
        this.store.upsertOne({ ...commande, tiers });
      }
    });
    if (this.store.getState().commandeClient()?.tiersId === tiers.id) {
      this.store.getWritableState().commandeClient.update((c) => {
        return { ...c, tiers };
      });
    }
  }

  @Untrack
  @UpdateStatus
  async loadAdresseLivraisonCommande(id: string) {
    return await firstValueFrom(this.api.loadAdresseLivraisonCommande(id));
  }

  @Untrack
  @UpdateStatus
  async cancel(a: CommandeClient) {
    const commande = await firstValueFrom(this.api.cancel(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async uncancel(a: CommandeClient) {
    const commande = await firstValueFrom(this.api.uncancel(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async confirm(a: CommandeClient) {
    const commande = await firstValueFrom(this.api.confirm(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async terminer(a: CommandeClient) {
    const commande = await firstValueFrom(this.api.terminer(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async reopen(a: CommandeClient) {
    const commande = await firstValueFrom(this.api.reopen(a));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
  }

  @Untrack
  @UpdateStatus
  async expedierVrac(payload: {
    stocks: VracExpeditionItem[];
    commandeClientId: string;
  }) {
    return await safeFirstValueFrom(this.api.expedierVrac(payload));
  }

  @Untrack
  @UpdateStatus
  async updateRemisePied(id: string, remisePied: number | null, remisePiedType: string | null, remisePiedLabel: string | null) {
    const commande = await firstValueFrom(this.api.updateRemisePied(id, remisePied, remisePiedType, remisePiedLabel));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
    return commande;
  }

  @Untrack
  @UpdateStatus
  async addEntrepot(ccId: string, adresseId: string) {
    const commande = await firstValueFrom(this.api.addEntrepot(ccId, adresseId));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
    return commande;
  }

  @Untrack
  @UpdateStatus
  async removeEntrepot(ccId: string, adresseId: string) {
    const commande = await firstValueFrom(this.api.removeEntrepot(ccId, adresseId));
    this.store.upsertOne(commande);
    if (this.store.getState().commandeClient()?.id === commande.id) {
      this.store.getWritableState().commandeClient.set(commande);
    }
    return commande;
  }
}
